/* eslint-disable @typescript-eslint/no-explicit-any */
import {getToken} from 'firebase/app-check';
import {appCheck, currentUser} from '@/services/FirestoreService';
import {parseResponse} from './parseResponse';
import type {AuthStrategy, HttpMethod, ProjectConfig, RequestOptions} from './types';

/**
 * A thin wrapper returned by HttpClient.withSchema() that binds a schema to all requests
 * made through it. The schema is forwarded to parseResponse for future Zod validation.
 *
 * Usage:
 *   const client = createHttpClient('INTERNAL');
 *   const result = await client.withSchema<UserInfo[]>(MySchema).get('retrieveFirebaseUsers');
 */
class SchemaBoundClient<T> {
  constructor(
    private readonly client: HttpClient,
    private readonly schema: unknown,
  ) {}

  get(functionName: string, options?: RequestOptions): Promise<T> {
    return this.client.get<T>(functionName, options, this.schema);
  }

  post(functionName: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.client.post<T>(functionName, body, options, this.schema);
  }

  put(functionName: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.client.put<T>(functionName, body, options, this.schema);
  }

  patch(functionName: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.client.patch<T>(functionName, body, options, this.schema);
  }

  delete(functionName: string, options?: RequestOptions): Promise<T> {
    return this.client.delete<T>(functionName, options, this.schema);
  }
}

/**
 * HTTP client for calling Google Cloud Functions.
 *
 * Manages auth token fetching internally - callers never need to obtain or pass an ID token.
 * Use `createHttpClient` from the index to instantiate.
 *
 * @example
 * const client = createHttpClient('INTERNAL');
 * const result = await client.post<MyType>('myCloudFunction', { key: 'value' });
 *
 * @example With future Zod schema validation:
 * const result = await client.withSchema<MyType>(MySchema).post('myCloudFunction', body);
 */
class HttpClient {
  constructor(
    private readonly config: ProjectConfig,
    private readonly authStrategy: AuthStrategy = 'bearer',
  ) {}

  /**
   * Builds the full URL for the given service/function name and optional sub-path by delegating
   * all environment and URL construction logic to the project config.
   */
  getUrl(functionName: string, path?: string): string {
    const isProd = process.env.VUE_APP_GCP_ENV === 'production';
    return this.config.getUrl(functionName, isProd, path);
  }

  /**
   * Returns a schema-bound client that forwards the given schema to parseResponse.
   * Intended for future Zod integration — no validation occurs until Zod is wired in.
   */
  withSchema<T>(schema: unknown): SchemaBoundClient<T> {
    return new SchemaBoundClient<T>(this, schema);
  }

  get<T = any>(functionName: string, options?: RequestOptions, schema?: unknown): Promise<T> {
    return this.request<T>('GET', functionName, undefined, options, schema);
  }

  post<T = any>(functionName: string, body?: unknown, options?: RequestOptions, schema?: unknown): Promise<T> {
    return this.request<T>('POST', functionName, body, options, schema);
  }

  put<T = any>(functionName: string, body?: unknown, options?: RequestOptions, schema?: unknown): Promise<T> {
    return this.request<T>('PUT', functionName, body, options, schema);
  }

  patch<T = any>(functionName: string, body?: unknown, options?: RequestOptions, schema?: unknown): Promise<T> {
    return this.request<T>('PATCH', functionName, body, options, schema);
  }

  delete<T = any>(functionName: string, options?: RequestOptions, schema?: unknown): Promise<T> {
    return this.request<T>('DELETE', functionName, undefined, options, schema);
  }

  private async request<T>(
    method: HttpMethod,
    functionName: string,
    body?: unknown,
    options: RequestOptions = {},
    schema?: unknown,
  ): Promise<T> {
    const {headers = {}, contentType = 'application/json', forceTokenRefresh = false, path} = options;

    const authHeader = await this.buildAuthHeader(forceTokenRefresh);

    const mergedHeaders: Record<string, string> = {
      ...authHeader,
      ...(contentType ? {'Content-Type': contentType} : {}),
      ...headers,
    };

    const fetchOptions: RequestInit = {method, headers: mergedHeaders};

    if (body !== undefined && body !== null) {
      fetchOptions.body =
        typeof body === 'string' || contentType !== 'application/json'
          ? (body as string)
          : JSON.stringify(body);
    }

    const url = this.getUrl(functionName, path);
    let response: Response;
    try {
      response = await fetch(url, fetchOptions);
    } catch (error) {
      throw new Error(`Fetch error for "${functionName}": ${(error as Error).message}`);
    }

    return parseResponse<T>(response, schema);
  }

  private async buildAuthHeader(forceTokenRefresh: boolean): Promise<Record<string, string>> {
    if (this.authStrategy === 'appcheck') {
      if (!appCheck) {
        throw new Error('App Check is not initialized. Cannot make App Check authenticated request.');
      }
      const tokenResult = await getToken(appCheck, false);
      return {'X-Firebase-Appcheck': tokenResult.token};
    }

    const idToken = await currentUser()?.getIdToken(forceTokenRefresh);
    if (!idToken) {
      throw new Error('Cannot make authenticated request: no current user.');
    }
    return {'Authorization': `Bearer ${idToken}`};
  }
}

export {HttpClient};
